/*
 * <bw-table-scroll> (sections/bw-compare, Oat & Mint T10 barkwoo-o0zk.18)
 * A data table that scrolls sideways inside its own region when it is wider
 * than the page (phones and small tablets). The element wraps the scroller
 * ([data-table-scroller], role="region" with a label) and keeps three
 * attributes in step with it for CSS (assets/bw-compare.css draws the line
 * beside the sticky feature column from data-more-start):
 *   data-overflow    the table is wider than the region;
 *   data-more-start  columns are hidden off the start edge (scrolled);
 *   data-more-end    columns are hidden off the end edge.
 * The scroller ships with tabindex="0" so keyboard users can scroll it even
 * before this runs (WCAG 2.1.1, axe "scrollable-region-focusable"); once it
 * runs, the tab stop stays only while the table actually overflows.
 */
class BwTableScroll extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-table-scroller]');
    if (!this.scroller) return;
    this.frame = 0;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this.onScroll);
    }
    this.update();
  }

  disconnectedCallback() {
    if (!this.scroller) return;
    this.scroller.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  update() {
    const scroller = this.scroller;
    const max = scroller.scrollWidth - scroller.clientWidth;
    const overflow = max > 1;
    // scrollLeft runs negative in right-to-left layouts; the distance is what matters.
    const offset = Math.abs(scroller.scrollLeft);
    this.toggleAttribute('data-overflow', overflow);
    this.toggleAttribute('data-more-start', overflow && offset > 1);
    this.toggleAttribute('data-more-end', overflow && offset < max - 1);
    if (overflow) {
      scroller.setAttribute('tabindex', '0');
    } else {
      scroller.removeAttribute('tabindex');
    }
  }
}

if (!customElements.get('bw-table-scroll')) {
  customElements.define('bw-table-scroll', BwTableScroll);
}
